import { DRAWING_LINE_WIDTH, addDays, asIsoDate, formatPrice } from '@stockstop/shared';
import {
  applyStroke,
  distanceToPoint,
  distanceToSegment,
  drawHandle,
  drawLabel,
  extendLine,
  HANDLE_HIT_RADIUS,
  HIT_TOLERANCE,
} from './geometry.js';
import type { DrawingRenderer, Point } from './types.js';

/**
 * (2) Diagonal support / resistance line between two anchors.
 *
 * Dragging an endpoint moves that anchor; dragging the line itself shifts both,
 * which is what keeps the slope intact while repositioning the whole line.
 */
export const trendLineRenderer: DrawingRenderer<'trend_line'> = {
  type: 'trend_line',

  handles(payload, context) {
    const { coordinates } = context;
    const fromX = coordinates.xForDate(payload.from.date);
    const fromY = coordinates.yForPrice(payload.from.price);
    const toX = coordinates.xForDate(payload.to.date);
    const toY = coordinates.yForPrice(payload.to.price);

    if (fromX === null || fromY === null || toX === null || toY === null) return [];
    return [
      { id: 'from', position: { x: fromX, y: fromY } },
      { id: 'to', position: { x: toX, y: toY } },
    ];
  },

  draw(payload, style, label, showPrice, context) {
    const { ctx, width } = context;
    const handles = this.handles(payload, context);
    if (handles.length < 2) return;

    const from = handles[0]!.position;
    const to = handles[1]!.position;
    const [start, end] = extendLine(
      from,
      to,
      { left: 0, right: width },
      payload.extendLeft,
      payload.extendRight,
    );

    ctx.save();
    /* Hilfslinien dürfen hinter die Kurse zurücktreten. */
    ctx.globalAlpha = style.opacity;
    applyStroke(
      ctx,
      style.color,
      DRAWING_LINE_WIDTH.trend_line + (context.isSelected ? 1 : 0),
      style.lineStyle,
    );
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    /* Beschriftung nur im Moment der Betrachtung — sonst überlädt sie den Chart. */
    if (context.isHovered || context.isSelected) {
      const caption = [
        label,
        showPrice ? formatPrice(payload.to.price, { currency: context.currency }) : null,
      ]
        .filter(Boolean)
        .join('  ');
      drawLabel(ctx, caption, { x: to.x + 8, y: to.y - 4 }, { color: style.color });
    }

    if (context.isSelected) {
      for (const handle of handles) drawHandle(ctx, handle.position, style.color);
    }
    ctx.restore();
  },

  hitTest(payload, point, context) {
    const handles = this.handles(payload, context);
    if (handles.length < 2) return null;

    for (const handle of handles) {
      if (distanceToPoint(point, handle.position) <= HANDLE_HIT_RADIUS) {
        return handle.id === 'from' ? 'from' : 'to';
      }
    }

    const [start, end] = extendLine(
      handles[0]!.position,
      handles[1]!.position,
      { left: 0, right: context.width },
      payload.extendLeft,
      payload.extendRight,
    );
    return distanceToSegment(point, start, end) <= HIT_TOLERANCE ? 'body' : null;
  },

  applyDrag(payload, handle, drag) {
    if (handle === 'from') {
      return { ...payload, from: { date: asIsoDate(drag.date), price: drag.price } };
    }
    if (handle === 'to') {
      return { ...payload, to: { date: asIsoDate(drag.date), price: drag.price } };
    }
    if (handle === 'body') {
      /* Shift both anchors by the same delta so the slope is preserved. */
      return {
        ...payload,
        from: {
          date: addDays(payload.from.date, drag.deltaDays),
          price: payload.from.price + drag.deltaPrice,
        },
        to: {
          date: addDays(payload.to.date, drag.deltaDays),
          price: payload.to.price + drag.deltaPrice,
        },
      };
    }
    return null;
  },
};

/** Re-exported for the channel, which draws a parallel pair of these lines. */
export const projectSegment = (
  from: Point,
  to: Point,
  width: number,
  extendLeft: boolean,
  extendRight: boolean,
): [Point, Point] => extendLine(from, to, { left: 0, right: width }, extendLeft, extendRight);
